import type { LDFlagValue } from '@vizit/feature-flags';
import { Heading, HeadingVariant } from '@vizit/molecules';

interface FlagDescriptor {
  /** LD flag key (e.g. "hero-score-formula"). */
  key: string;
  /** Human-readable display name (curated; the LD client SDK doesn't expose this). */
  name: string;
  /** Short description of what the flag controls (curated). */
  description: string;
}

interface FlagGroup {
  /** Section title (e.g. "October 5 release"). */
  title: string;
  /** Curated flags shown under this title, in order. */
  flags: readonly FlagDescriptor[];
}

interface OrganizationFeatureFlagsProps {
  /** Curated groups rendered on top, in order. */
  groups: readonly FlagGroup[];
  /** Every flag LD resolved for the org (the client's `allFlags()`), keyed by flag key. */
  values: Record<string, LDFlagValue>;
  /** True while the underlying LD client is resolving. */
  isLoading: boolean;
  /** Non-null if the LD client failed to initialize. */
  error: Error | null;
}

const ORG_CONTEXT_CAVEAT =
  'Evaluated with organization context only. Flags targeted by user, workspace, or segment attributes may differ for individual users in this org.';

const NOT_CLIENT_SIDE = 'Not available client-side';

function renderVariation(values: Record<string, LDFlagValue>, key: string): string {
  if (!(key in values)) return NOT_CLIENT_SIDE;
  const value = values[key];
  if (value === undefined || value === null) return '—';
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (typeof value === 'object') return JSON.stringify(value);
  if (value === '') return '(empty)';
  return String(value);
}

function otherFlagKeys(groups: readonly FlagGroup[], values: Record<string, LDFlagValue>): string[] {
  const curated = new Set(groups.flatMap((group) => group.flags.map((flag) => flag.key)));
  return Object.keys(values)
    .filter((key) => !curated.has(key))
    .sort();
}

/**
 * Presentational panel listing LaunchDarkly flags with their current variation
 * for an organization. Curated groups come first with a name and description;
 * everything else LD resolved is tucked under a collapsed "All other flags"
 * disclosure with its raw key, so new rollouts show up without a code change.
 * A caveat about the org-only evaluation context always closes the panel,
 * because it applies to curated and uncurated flags alike.
 *
 * Intentionally bare-bones: this is an internal admin surface that prioritizes
 * clarity over visual polish.
 */
function OrganizationFeatureFlags(props: OrganizationFeatureFlagsProps) {
  const { groups, values, isLoading, error } = props;
  const otherKeys = otherFlagKeys(groups, values);

  return (
    <div className="flex flex-col gap-8">
      <Heading variant={HeadingVariant.H5}>Feature flags</Heading>
      {isLoading ? (
        <p className="text-sm text-secondary">Loading feature flags…</p>
      ) : error ? (
        <p className="text-sm text-error">Failed to load feature flags: {error.message}</p>
      ) : (
        <>
          {groups.map((group) => (
            <section key={group.title} className="flex flex-col gap-8">
              <Heading variant={HeadingVariant.H6}>{group.title}</Heading>
              <ul className="flex flex-col gap-12 text-sm">
                {group.flags.map(({ key, name, description }) => (
                  <li key={key} className="flex flex-col gap-2">
                    <div className="flex items-baseline justify-between gap-8">
                      <span className="font-medium">{name}</span>
                      <span className="min-w-0 break-all text-right font-mono text-secondary">
                        {renderVariation(values, key)}
                      </span>
                    </div>
                    <span className="text-tertiary text-xs">{description}</span>
                    <span className="font-mono text-quaternary text-xs">{key}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {otherKeys.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer font-medium">All other flags ({otherKeys.length})</summary>
              <ul className="mt-8 flex flex-col gap-4 font-mono">
                {otherKeys.map((key) => (
                  <li key={key} className="flex items-baseline justify-between gap-8">
                    <span>{key}</span>
                    <span className="min-w-0 break-all text-right text-secondary">{renderVariation(values, key)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          <p className="text-tertiary text-xs">{ORG_CONTEXT_CAVEAT}</p>
        </>
      )}
    </div>
  );
}

export { OrganizationFeatureFlags };
export type { FlagDescriptor, FlagGroup, OrganizationFeatureFlagsProps };
